import classNames from 'classnames';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Dropdown, DropdownChangeParams } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Dispatch, useMemo } from 'react';
import { useState } from 'react';
import { SetStateAction } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useDebounce } from 'use-lodash-debounce';

import useAxiosHook from '../../../hooks/useAxiosHook';
import useHaveValuesChanged from '../../../hooks/useHaveValuesChanged';
import useSearchQueryDropdownParam from '../../../hooks/useSearchQueryDropdownParam';
import useSearchQueryParam from '../../../hooks/useSearchQueryParam';
import { WithData } from '../../../types/api';
import { debounceTimeout } from '../../../utils/constants/misc';
import { getSearchQueryParam } from '../../../utils/helpers/searchQuery';
import FiltersCounter from '../Components/Filters/FiltersCounter';
import { LogisticsRoute } from '../LogisticsRoutes/LogisticsRoutes.functions';
import { TransportEntitiesTypes } from './TransportEntities.functions';

function useTableFilters(
  page: number,
  setPage: Dispatch<SetStateAction<number>>,
  limit: number
) {
  const { t } = useTranslation();
  const location = useLocation();
  const [barcodeFilter, setBarcodeFilter] = useState(
    () => getSearchQueryParam(location.search, 'barcode') ?? ''
  );
  const [ownerLogisticPointId, setOwnerLogisticPointId] = useState(
    () => getSearchQueryParam(location.search, 'ownerLogisticPointId') ?? ''
  );
  const [currentLogisticPointId, setCurrentLogisticPointId] = useState(
    () => getSearchQueryParam(location.search, 'currentLogisticPointId') ?? ''
  );
  const [transportEntityTypeFilter, setTransportEntityTypeFilter] = useState(
    () => getSearchQueryParam(location.search, 'transportEntityTypeId') ?? ''
  );

  const debouncedBarcodeFilter = useDebounce(barcodeFilter, debounceTimeout);

  const filtersArr = useMemo(
    () => [
      debouncedBarcodeFilter,
      ownerLogisticPointId,
      currentLogisticPointId,
      transportEntityTypeFilter,
    ],
    [
      debouncedBarcodeFilter,
      ownerLogisticPointId,
      currentLogisticPointId,
      transportEntityTypeFilter,
    ]
  );

  const haveFiltersChanged = useHaveValuesChanged(filtersArr);

  useEffect(() => {
    if (!setPage) {
      return;
    }
    setPage(1);
  }, [
    setPage,
    debouncedBarcodeFilter,
    transportEntityTypeFilter,
    currentLogisticPointId,
    ownerLogisticPointId,
    haveFiltersChanged,
  ]);

  const {
    data: transportEntityTypes,
    error: transportEntityTypesError,
    isLoading: isTransportEntityTypesLoading,
  } = useAxiosHook<WithData<TransportEntitiesTypes[]>>(
    '/v2/transport-entity-types'
  );
  const {
    data: logisticPoints,
    error: logisticPointsError,
    isLoading: isLogisticPointsLoading,
  } = useAxiosHook<WithData<LogisticsRoute[]>>(
    '/logistics-point?pointTypeId=1'
  );

  const transportEntityTypesOptions = useMemo(
    () =>
      transportEntityTypes?.data.map((type) => ({
        value: type.id,
        label: type.name,
      })) ?? [],
    [transportEntityTypes]
  );
  const logisticPointsOptions = useMemo(
    () =>
      logisticPoints?.data.map((type) => ({
        value: type.id,
        label: type.name,
      })) ?? [],
    [logisticPoints]
  );

  useSearchQueryParam('barcode', debouncedBarcodeFilter);
  useSearchQueryDropdownParam(
    'ownerLogisticPointId',
    ownerLogisticPointId,
    setOwnerLogisticPointId,
    logisticPointsOptions
  );
  useSearchQueryDropdownParam(
    'currentLogisticPointId',
    currentLogisticPointId,
    setCurrentLogisticPointId,
    logisticPointsOptions
  );
  useSearchQueryDropdownParam(
    'transportEntityType',
    transportEntityTypeFilter,
    setTransportEntityTypeFilter,
    transportEntityTypesOptions
  );

  const httpFiltersObj = useMemo(() => {
    return {
      page: haveFiltersChanged ? 1 : page,
      limit,
      barcode: debouncedBarcodeFilter,
      transportEntityTypeId: transportEntityTypeFilter,
      currentLogisticPointId: currentLogisticPointId,
      ownerLogisticPointId: ownerLogisticPointId,
    };
  }, [
    debouncedBarcodeFilter,
    haveFiltersChanged,
    transportEntityTypeFilter,
    currentLogisticPointId,
    ownerLogisticPointId,
    limit,
    page,
  ]);

  const basicFiltersActiveFilterCount = Object.values({
    barcodeFilter,
    ownerLogisticPointId,
    currentLogisticPointId,
    transportEntityTypeFilter,
  }).filter(Boolean).length;

  const filters = useMemo<JSX.Element>(
    () => (
      <Accordion multiple activeIndex={[0]}>
        <AccordionTab
          disabled
          headerTemplate={
            <FiltersCounter
              description={t('Basic filters')}
              counter={basicFiltersActiveFilterCount}
            />
          }
        >
          <div className="sidebar_filter">
            <label htmlFor="filter_barcode">{t('Barcode')}</label>
            <InputText
              id="filter_barcode"
              value={barcodeFilter}
              onChange={(e) => setBarcodeFilter(e.target.value)}
              className={classNames({
                sidebar_filter_active: barcodeFilter,
              })}
            />
          </div>
          <div className="sidebar_filter">
            <label htmlFor="filter_owner_logistic_point">
              {t('Owner logistic point')}
            </label>
            <Dropdown
              id="filter_owner_logistic_point"
              options={logisticPointsOptions}
              value={ownerLogisticPointId}
              showClear
              placeholder={t('All')}
              onChange={(e: DropdownChangeParams) =>
                setOwnerLogisticPointId(e.value)
              }
              disabled={!!logisticPointsError || isLogisticPointsLoading}
              className={classNames({
                sidebar_filter_active: ownerLogisticPointId,
              })}
            />
          </div>
          <div className="sidebar_filter">
            <label htmlFor="filter_current_logistic_point">
              {t('Current logistic point')}
            </label>
            <Dropdown
              id="filter_current_logistic_point"
              options={logisticPointsOptions}
              value={currentLogisticPointId}
              showClear
              filter
              placeholder={t('All')}
              onChange={(e: DropdownChangeParams) =>
                setCurrentLogisticPointId(e.value)
              }
              disabled={!!logisticPointsError || isLogisticPointsLoading}
              className={classNames({
                sidebar_filter_active: currentLogisticPointId,
              })}
            />
          </div>
          <div className="sidebar_filter">
            <label htmlFor="filter_transport_entity_type">
              {t('Transport entity type')}
            </label>
            <Dropdown
              id="filter_transport_entity_type"
              options={transportEntityTypesOptions}
              value={transportEntityTypeFilter}
              showClear
              filter
              placeholder={t('All')}
              onChange={(e: DropdownChangeParams) =>
                setTransportEntityTypeFilter(e.value)
              }
              disabled={
                isTransportEntityTypesLoading || !!transportEntityTypesError
              }
              className={classNames({
                sidebar_filter_active: transportEntityTypeFilter,
              })}
            />
          </div>
        </AccordionTab>
      </Accordion>
    ),
    [
      basicFiltersActiveFilterCount,
      barcodeFilter,
      ownerLogisticPointId,
      currentLogisticPointId,
      transportEntityTypeFilter,
      transportEntityTypesOptions,
      isTransportEntityTypesLoading,
      transportEntityTypesError,
      logisticPointsOptions,
      logisticPointsError,
      isLogisticPointsLoading,
      setTransportEntityTypeFilter,
      setBarcodeFilter,
      setOwnerLogisticPointId,
      setCurrentLogisticPointId,
      t,
    ]
  );

  const headerFiltersCount = useMemo(
    () => basicFiltersActiveFilterCount,
    [basicFiltersActiveFilterCount]
  );

  function resetAllFilters() {
    setBarcodeFilter('');
    setTransportEntityTypeFilter('');
    setCurrentLogisticPointId('');
    setOwnerLogisticPointId('');
  }

  return {
    filters,
    resetAllFilters,
    httpFiltersObj,
    headerFiltersCount,
  };
}

export default useTableFilters;
